import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query"
import { api } from "../../api"
import { setAlertFunc } from "../../components/AlertToaster/AlertSlice"
import { TStore } from "../../store/Store"
import { IFile, UserInfo } from "../Common/commonTypes"
import { resetProfileSlice, setOtpToken } from "./features/Profile/ProfileSlice"

const MODULE_URL = "settings"

type GetProfileResponse = {
    commenter_colour: string
    commenter_initials: string
    email: string
    phone: number
    position_name: string
    role: string
    team_name: string
    username: string
}

type VerfiyOtpResponse = {
    message: string
    status: number
    token: {
        otp_token: string
    }
}

export type NotificationConfig = {
    action_data: {
        channel: {
            email: 0 | 1
            in_app: 0 | 1
            push: 0 | 1
        }
        schedule: string
    }
    action_description: string
    action_id: number
    action_name: string
    actions_enabled: number
}

type NotificationConfigResponse = {
    data: NotificationConfig[]
    notifications_enabled: 0 | 1
}

type ReasonRequest = {
    reason?: string
    id?: number
    text_flag: boolean
    status?: boolean
    reason_data?: {
        id: any
        reason: any
    }[]
}

type FormatObj = {
    category_id: number
    description: string
    id: number
    link: IFile
    name: string
    status: boolean
    uploaded_by: UserInfo
    uploaded_on: string
}

type FormatResponse = {
    data: FormatObj[]
    module_name: string
}

export type RulesObj = {
    ev_application_rule: boolean
    id: number
    name: string
}

const settingsService = api.injectEndpoints({
    endpoints: build => ({
        /****************************** Profile Queries ******************************/
        getProfile: build.query<GetProfileResponse, void>({
            query: () => ({
                url: `${MODULE_URL}/listprofile`,
            }),
            providesTags: [{ type: "Settings", id: "Profile" }],
        }),
        // to be later shifted to auth service
        sendOTP: build.mutation<{ message: string }, { phone: string }>({
            query: body => ({
                url: `login/send-otp`,
                method: "POST",
                body: body,
            }),
        }),
        resendOTP: build.mutation<{ message: string }, { phone: string }>({
            query: body => ({
                url: `login/resend-otp`,
                method: "POST",
                body: body,
            }),
        }),
        verifyOTP: build.mutation<VerfiyOtpResponse, { phone: string; otp: string }>({
            query: body => ({
                url: `login/verify-otp`,
                method: "POST",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                let res = await queryFulfilled
                if (res.data.token?.otp_token) {
                    dispatch(setOtpToken(res.data.token?.otp_token))
                }
            },
        }),
        editProfile: build.mutation<{ message: string }, { phone?: string; username: string; email: string }>({
            queryFn: async (body, { dispatch, getState }, extraOptions, baseQuery) => {
                const store = getState() as TStore
                const otpToken = store.Profile.otp_tokens
                const result = await baseQuery({
                    url: `${MODULE_URL}/editprofile`,
                    method: "PUT",
                    headers: {
                        "otp-token": otpToken,
                    },
                    body,
                })
                if (result.data && typeof result.data === "object" && "message" in result.data) {
                    const message = (result.data as { message: string }).message
                    dispatch(resetProfileSlice())
                    dispatch(setAlertFunc({ type: "success", msg: message }))
                    return { data: result.data as { message: string } }
                } else {
                    dispatch(setAlertFunc({ type: "error", msg: "Profile updation failed!" }))
                    return { error: result.error as FetchBaseQueryError }
                }
            },
            invalidatesTags: [{ type: "Settings", id: "Profile" }],
        }),
        getReportTemplates: build.query<{ link: IFile }, { isCharger: boolean }>({
            query: ({ isCharger }) => ({
                url: `${MODULE_URL}/report_templates`,
                params: {
                    is_charger: isCharger,
                },
                useV3: true,
            }),
            transformResponse: (
                res: { charger_template?: { link: IFile }; material_template?: { link: IFile } },
                _,
                arg
            ) => (arg.isCharger ? res.charger_template : res.material_template) as { link: IFile },
        }),
        /****************************** Notifications Queries ******************************/
        getNotificationConfig: build.query<NotificationConfigResponse, void>({
            query: () => ({
                url: `notifications/acl`,
            }),
            providesTags: [{ type: "Settings", id: "Notifications" }],
        }),
        editNotificationConfig: build.mutation<unknown, { data: NotificationConfig[] }>({
            query: body => ({
                url: `notifications/acl`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: [{ type: "Settings", id: "Notifications" }],
        }),
        /****************************** Reason Queries ******************************/
        addReasons: build.mutation<
            { message: string },
            { module_id: number; submodule_id: number; reason_type: string; reason: string }
        >({
            query: body => ({
                url: `${MODULE_URL}/reasons`,
                method: "POST",
                body: {
                    ...body,
                    submodule_id: body.submodule_id ?? "",
                },
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: [{ type: "Settings", id: "Reasons" }],
        }),
        editReasons: build.mutation<{ message: string }, ReasonRequest>({
            query: body => ({
                url: `${MODULE_URL}/reasons`,
                method: "PUT",
                body: body,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    dispatch(setAlertFunc({ type: "success", msg: data?.message }))
                } catch (error: any) {
                    dispatch(setAlertFunc({ type: "error", msg: error.message }))
                }
            },
            invalidatesTags: [{ type: "Settings", id: "Reasons" }],
        }),
        /****************************** Template Queries ******************************/
        getFormat: build.query<FormatResponse[], { module_id: number; modules_flag: 0 | 1 }>({
            query: params => ({
                url: `${MODULE_URL}/upload_report_formats`,
                params: params,
            }),
            providesTags: [{ type: "Settings", id: "Template" }],
        }),
        editFormat: build.mutation({
            query: body => ({
                url: `${MODULE_URL}/upload_report_formats`,
                method: "PUT",
                body,
            }),
            invalidatesTags: [{ type: "Settings", id: "Template" }],
        }),
        /****************************** Rules Queries ******************************/
        getRules: build.query<RulesObj[], void>({
            query: () => ({
                url: `${MODULE_URL}/staterule`,
            }),
            providesTags: [{ type: "Settings", id: "Rules" }],
        }),
        editRules: build.mutation<unknown, { data: RulesObj[] }>({
            query: body => ({
                url: `${MODULE_URL}/staterule`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: [{ type: "Settings", id: "Rules" }],
        }),
    }),
})

export const {
    useGetProfileQuery,
    useSendOTPMutation,
    useResendOTPMutation,
    useVerifyOTPMutation,
    useEditProfileMutation,

    useGetReportTemplatesQuery,

    useGetNotificationConfigQuery,
    useEditNotificationConfigMutation,

    useAddReasonsMutation,
    useEditReasonsMutation,

    useGetFormatQuery,
    useEditFormatMutation,

    useGetRulesQuery,
    useEditRulesMutation,
} = settingsService

export { settingsService }
